'use client'

import {
    getTimelineByClient,
    MedicalTimelineEntry,
    TIMELINE_EVENT_TYPE_LABELS,
    TimelineEventType,
} from '@/lib/api/prontuario/prontuario-api'
import { format, parseISO, subMonths } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useCallback, useEffect, useState } from 'react'
import {
    MdAssignment,
    MdCalendarToday,
    MdChevronRight,
    MdDescription,
    MdFilterList,
    MdKeyboardArrowDown,
    MdKeyboardArrowUp,
    MdLocalHospital,
    MdMedicalServices,
    MdOutlineEdit,
    MdOutlineEventNote,
    MdOutlineExitToApp,
    MdOutlineMedication,
    MdOutlineMonitorHeart,
    MdOutlineNoteAlt,
    MdOutlineScience,
    MdOutlineSend,
    MdOutlineWarning,
    MdRefresh,
    MdTimeline,
    MdVaccines
} from 'react-icons/md'
import { toast } from 'react-toastify'
import { useProntuarioData, useProntuarioNavigate } from './prontuario-context'

const ITEMS_PER_PAGE = 20

const TIMELINE_EVENT_ICONS: Record<TimelineEventType, React.ReactNode> = {
    [TimelineEventType.TIMELINE_CONSULTATION]: (
        <MdMedicalServices className="h-5 w-5" />
    ),
    [TimelineEventType.TIMELINE_TRIAGE]: (
        <MdOutlineMonitorHeart className="h-5 w-5" />
    ),
    [TimelineEventType.TIMELINE_EVOLUTION]: <MdOutlineEdit className="h-5 w-5" />,
    [TimelineEventType.TIMELINE_PRESCRIPTION]: (
        <MdOutlineMedication className="h-5 w-5" />
    ),
    [TimelineEventType.TIMELINE_EXAM_REQUEST]: (
        <MdOutlineScience className="h-5 w-5" />
    ),
    [TimelineEventType.TIMELINE_EXAM_RESULT]: (
        <MdOutlineScience className="h-5 w-5" />
    ),
    [TimelineEventType.TIMELINE_PROCEDURE]: (
        <MdLocalHospital className="h-5 w-5" />
    ),
    [TimelineEventType.TIMELINE_DIAGNOSIS]: <MdAssignment className="h-5 w-5" />,
    [TimelineEventType.TIMELINE_ALLERGY_REGISTERED]: (
        <MdOutlineWarning className="h-5 w-5" />
    ),
    [TimelineEventType.TIMELINE_REFERRAL]: <MdOutlineSend className="h-5 w-5" />,
    [TimelineEventType.TIMELINE_CERTIFICATE]: (
        <MdDescription className="h-5 w-5" />
    ),
    [TimelineEventType.TIMELINE_REPORT]: <MdDescription className="h-5 w-5" />,
    [TimelineEventType.TIMELINE_DOCUMENT_SIGNED]: (
        <MdOutlineEventNote className="h-5 w-5" />
    ),
    [TimelineEventType.TIMELINE_HOSPITALIZATION]: (
        <MdLocalHospital className="h-5 w-5" />
    ),
    [TimelineEventType.TIMELINE_DISCHARGE]: (
        <MdOutlineExitToApp className="h-5 w-5" />
    ),
    [TimelineEventType.TIMELINE_VACCINATION]: <MdVaccines className="h-5 w-5" />,
    [TimelineEventType.TIMELINE_NOTE]: <MdOutlineNoteAlt className="h-5 w-5" />,
}

const TIMELINE_EVENT_COLORS: Record<TimelineEventType, string> = {
    [TimelineEventType.TIMELINE_CONSULTATION]: 'bg-blue-500',
    [TimelineEventType.TIMELINE_TRIAGE]: 'bg-purple-500',
    [TimelineEventType.TIMELINE_EVOLUTION]: 'bg-green-500',
    [TimelineEventType.TIMELINE_PRESCRIPTION]: 'bg-orange-500',
    [TimelineEventType.TIMELINE_EXAM_REQUEST]: 'bg-cyan-500',
    [TimelineEventType.TIMELINE_EXAM_RESULT]: 'bg-teal-500',
    [TimelineEventType.TIMELINE_PROCEDURE]: 'bg-red-500',
    [TimelineEventType.TIMELINE_DIAGNOSIS]: 'bg-yellow-600',
    [TimelineEventType.TIMELINE_ALLERGY_REGISTERED]: 'bg-red-600',
    [TimelineEventType.TIMELINE_REFERRAL]: 'bg-indigo-500',
    [TimelineEventType.TIMELINE_CERTIFICATE]: 'bg-gray-600',
    [TimelineEventType.TIMELINE_REPORT]: 'bg-gray-500',
    [TimelineEventType.TIMELINE_DOCUMENT_SIGNED]: 'bg-emerald-500',
    [TimelineEventType.TIMELINE_HOSPITALIZATION]: 'bg-rose-600',
    [TimelineEventType.TIMELINE_DISCHARGE]: 'bg-lime-600',
    [TimelineEventType.TIMELINE_VACCINATION]: 'bg-sky-500',
    [TimelineEventType.TIMELINE_NOTE]: 'bg-slate-500',
}

export default function TimelinePage() {
    const navigate = useProntuarioNavigate()
    const { client, clientId } = useProntuarioData()

    const [entries, setEntries] = useState<MedicalTimelineEntry[]>([])
    const [loading, setLoading] = useState(true)
    const [currentPage, setCurrentPage] = useState(1)
    const [hasMore, setHasMore] = useState(true)
    const [totalLoaded, setTotalLoaded] = useState(0)

    // Filter states
    const [showFilters, setShowFilters] = useState(false)
    const [selectedEventType, setSelectedEventType] = useState<
        TimelineEventType | ''
    >('')
    const [startDate, setStartDate] = useState<string>(
        format(subMonths(new Date(), 12), 'yyyy-MM-dd')
    )
    const [endDate, setEndDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'))

    // Expanded entries for showing details
    const [expandedEntries, setExpandedEntries] = useState<Set<number>>(new Set())

    const loadTimeline = useCallback(
        async (page: number = 1, append: boolean = false) => {
            if (!clientId) return

            try {
                setLoading(true)
                const response = await getTimelineByClient(parseInt(clientId), {
                    eventType: selectedEventType || undefined,
                    startDate: startDate || undefined,
                    endDate: endDate || undefined,
                    page,
                    limit: ITEMS_PER_PAGE,
                })

                if (response.success && response.result) {
                    if (append) {
                        setEntries((prev) => [...prev, ...response.result!])
                    } else {
                        setEntries(response.result)
                    }
                    setHasMore(response.result.length === ITEMS_PER_PAGE)
                    setTotalLoaded(
                        append
                            ? totalLoaded + response.result.length
                            : response.result.length
                    )
                }
            } catch (error) {
                toast.error('Erro ao carregar timeline')
            } finally {
                setLoading(false)
            }
        },
        [clientId, selectedEventType, startDate, endDate, totalLoaded]
    )

    useEffect(() => {
        loadTimeline(1, false)
    }, [clientId])

    const handleApplyFilters = () => {
        setCurrentPage(1)
        setExpandedEntries(new Set())
        loadTimeline(1, false)
    }

    const handleClearFilters = () => {
        setSelectedEventType('')
        setStartDate(format(subMonths(new Date(), 12), 'yyyy-MM-dd'))
        setEndDate(format(new Date(), 'yyyy-MM-dd'))
        setCurrentPage(1)
        setExpandedEntries(new Set())
        setTimeout(() => loadTimeline(1, false), 0)
    }

    const handleLoadMore = () => {
        const nextPage = currentPage + 1
        setCurrentPage(nextPage)
        loadTimeline(nextPage, true)
    }

    const toggleEntryExpanded = (id: number) => {
        setExpandedEntries((prev) => {
            const newSet = new Set(prev)
            if (newSet.has(id)) {
                newSet.delete(id)
            } else {
                newSet.add(id)
            }
            return newSet
        })
    }

    const formatEventDate = (dateStr: string) => {
        try {
            const date = parseISO(dateStr)
            return format(date, "dd 'de' MMMM 'de' yyyy, HH:mm", { locale: ptBR })
        } catch {
            return dateStr
        }
    }

    const formatDateShort = (dateStr: string) => {
        try {
            const date = parseISO(dateStr)
            return format(date, 'dd/MM/yyyy', { locale: ptBR })
        } catch {
            return dateStr
        }
    }

    const groupEntriesByMonth = (
        entries: MedicalTimelineEntry[]
    ): Map<string, MedicalTimelineEntry[]> => {
        const groups = new Map<string, MedicalTimelineEntry[]>()

        entries.forEach((entry) => {
            try {
                const date = parseISO(entry.eventDate)
                const monthKey = format(date, 'MMMM yyyy', { locale: ptBR })

                if (!groups.has(monthKey)) {
                    groups.set(monthKey, [])
                }
                groups.get(monthKey)!.push(entry)
            } catch {
                // Skip invalid dates
            }
        })

        return groups
    }

    const groupedEntries = groupEntriesByMonth(entries)

    const handleQuickFilter = (months: number) => {
        const end = new Date()
        const start = subMonths(end, months)
        setStartDate(format(start, 'yyyy-MM-dd'))
        setEndDate(format(end, 'yyyy-MM-dd'))
    }

    if (loading && entries.length === 0) {
        return (
            <div className="flex items-center justify-center py-12">
                <span className="loading loading-spinner loading-lg"></span>
            </div>
        )
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                    <MdTimeline className="h-6 w-6 text-secondary" />
                    <h2 className="text-xl font-semibold">Timeline Médica</h2>
                </div>

                <div className="flex gap-2">
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className={`btn btn-sm ${showFilters ? 'btn-secondary' : 'btn-outline'}`}
                    >
                        <MdFilterList className="h-4 w-4" />
                        Filtros
                    </button>
                    <button
                        onClick={() => loadTimeline(1, false)}
                        className="btn btn-outline btn-sm"
                    >
                        <MdRefresh className="h-4 w-4" />
                        Atualizar
                    </button>
                </div>
            </div>

            {/* Filters Panel */}
            {showFilters && (
                <div className="card bg-base-200">
                    <div className="card-body gap-4">
                        <h3 className="font-medium">Filtros</h3>

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                            {/* Event Type Filter */}
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Tipo de Evento</span>
                                </label>
                                <select
                                    className="select select-bordered select-sm"
                                    value={selectedEventType}
                                    onChange={(e) =>
                                        setSelectedEventType(
                                            e.target.value as TimelineEventType
                                        )
                                    }
                                >
                                    <option value="">Todos os tipos</option>
                                    {Object.entries(TIMELINE_EVENT_TYPE_LABELS).map(
                                        ([value, label]) => (
                                            <option key={value} value={value}>
                                                {label}
                                            </option>
                                        )
                                    )}
                                </select>
                            </div>

                            {/* Start Date */}
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Data Inicial</span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered input-sm"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                />
                            </div>

                            {/* End Date */}
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Data Final</span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered input-sm"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                />
                            </div>

                            {/* Quick Filters */}
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Período Rápido</span>
                                </label>
                                <div className="flex gap-1">
                                    <button
                                        className="btn btn-outline btn-xs"
                                        onClick={() => handleQuickFilter(1)}
                                    >
                                        1M
                                    </button>
                                    <button
                                        className="btn btn-outline btn-xs"
                                        onClick={() => handleQuickFilter(3)}
                                    >
                                        3M
                                    </button>
                                    <button
                                        className="btn btn-outline btn-xs"
                                        onClick={() => handleQuickFilter(6)}
                                    >
                                        6M
                                    </button>
                                    <button
                                        className="btn btn-outline btn-xs"
                                        onClick={() => handleQuickFilter(12)}
                                    >
                                        1A
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-2">
                            <button
                                onClick={handleClearFilters}
                                className="btn btn-ghost btn-sm"
                            >
                                Limpar
                            </button>
                            <button
                                onClick={handleApplyFilters}
                                className="btn btn-secondary btn-sm"
                            >
                                Aplicar Filtros
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Timeline */}
            {entries.length === 0 ? (
                <div className="card bg-base-200">
                    <div className="card-body items-center py-12 text-center">
                        <MdTimeline className="h-12 w-12 text-base-content/30" />
                        <p className="text-base-content/60">
                            Nenhum evento encontrado para o período selecionado.
                        </p>
                    </div>
                </div>
            ) : (
                <div className="space-y-8">
                    {Array.from(groupedEntries.entries()).map(
                        ([monthYear, monthEntries]) => (
                            <div key={monthYear}>
                                {/* Month Header */}
                                <div className="sticky top-0 z-10 mb-4 flex items-center gap-2 bg-base-100 py-2">
                                    <MdCalendarToday className="h-5 w-5 text-secondary" />
                                    <h3 className="text-lg font-semibold capitalize">
                                        {monthYear}
                                    </h3>
                                    <span className="badge badge-ghost badge-sm">
                                        {monthEntries.length} evento
                                        {monthEntries.length !== 1 ? 's' : ''}
                                    </span>
                                </div>

                                {/* Timeline Events */}
                                <div className="relative ml-6 border-l-2 border-base-300 pl-6">
                                    {monthEntries.map((entry, index) => {
                                        const isExpanded = expandedEntries.has(entry.id)
                                        const eventColor =
                                            TIMELINE_EVENT_COLORS[entry.eventType] ||
                                            'bg-gray-500'
                                        const eventIcon =
                                            TIMELINE_EVENT_ICONS[entry.eventType] || (
                                                <MdOutlineNoteAlt className="h-5 w-5" />
                                            )

                                        return (
                                            <div
                                                key={entry.id}
                                                className={`relative mb-6 ${index === monthEntries.length - 1 ? '' : ''}`}
                                            >
                                                {/* Timeline Dot */}
                                                <div
                                                    className={`absolute -left-[35px] flex h-6 w-6 items-center justify-center rounded-full text-white ${eventColor}`}
                                                >
                                                    {eventIcon}
                                                </div>

                                                {/* Event Card */}
                                                <div className="card bg-base-200 transition-shadow hover:shadow-md">
                                                    <div className="card-body p-4">
                                                        <div className="flex items-start justify-between gap-4">
                                                            <div className="flex-1">
                                                                <div className="mb-1 flex flex-wrap items-center gap-2">
                                                                    <span
                                                                        className={`badge badge-sm text-white ${eventColor}`}
                                                                    >
                                                                        {
                                                                            TIMELINE_EVENT_TYPE_LABELS[
                                                                            entry.eventType
                                                                            ]
                                                                        }
                                                                    </span>
                                                                    <span className="text-sm text-base-content/60">
                                                                        {formatEventDate(
                                                                            entry.eventDate
                                                                        )}
                                                                    </span>
                                                                </div>

                                                                <h4 className="font-medium">
                                                                    {entry.title}
                                                                </h4>

                                                                {entry.professional && (
                                                                    <p className="mt-1 text-sm text-base-content/70">
                                                                        Por:{' '}
                                                                        {entry.professional.name}
                                                                        {entry.professional
                                                                            .registrationNumber && (
                                                                                <span className="text-base-content/50">
                                                                                    {' '}
                                                                                    -{' '}
                                                                                    {
                                                                                        entry
                                                                                            .professional
                                                                                            .councilName
                                                                                    }{' '}
                                                                                    {
                                                                                        entry
                                                                                            .professional
                                                                                            .registrationNumber
                                                                                    }
                                                                                </span>
                                                                            )}
                                                                    </p>
                                                                )}

                                                                {isExpanded &&
                                                                    entry.summary && (
                                                                        <div className="mt-3 border-t border-base-300 pt-3">
                                                                            <p className="whitespace-pre-wrap text-sm">
                                                                                {entry.summary}
                                                                            </p>
                                                                        </div>
                                                                    )}

                                                                {isExpanded &&
                                                                    entry.metadata && (
                                                                        <div className="mt-3 rounded-lg bg-base-300 p-3">
                                                                            <pre className="overflow-x-auto text-xs">
                                                                                {JSON.stringify(
                                                                                    entry.metadata,
                                                                                    null,
                                                                                    2
                                                                                )}
                                                                            </pre>
                                                                        </div>
                                                                    )}
                                                            </div>

                                                            <button
                                                                onClick={() =>
                                                                    toggleEntryExpanded(entry.id)
                                                                }
                                                                className="btn btn-circle btn-ghost btn-sm"
                                                            >
                                                                {isExpanded ? (
                                                                    <MdKeyboardArrowUp className="h-5 w-5" />
                                                                ) : (
                                                                    <MdKeyboardArrowDown className="h-5 w-5" />
                                                                )}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )
                    )}

                    {/* Load More */}
                    {hasMore && (
                        <div className="flex justify-center pt-4">
                            <button
                                onClick={handleLoadMore}
                                className="btn btn-outline"
                                disabled={loading}
                            >
                                {loading ? (
                                    <span className="loading loading-spinner loading-sm"></span>
                                ) : (
                                    <>
                                        Carregar Mais
                                        <MdChevronRight className="h-5 w-5" />
                                    </>
                                )}
                            </button>
                        </div>
                    )}

                    {/* Summary */}
                    <div className="text-center text-sm text-base-content/60">
                        Exibindo {entries.length} evento{entries.length !== 1 ? 's' : ''}
                        {selectedEventType && (
                            <span>
                                {' '}
                                do tipo{' '}
                                <strong>
                                    {TIMELINE_EVENT_TYPE_LABELS[selectedEventType]}
                                </strong>
                            </span>
                        )}
                        {startDate && endDate && (
                            <span>
                                {' '}
                                entre {formatDateShort(startDate)} e{' '}
                                {formatDateShort(endDate)}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}
